import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaBell, FaCheck, FaCheckDouble, FaSpinner, FaArrowRight } from 'react-icons/fa';
import ClientLayout from '../layouts/ClientLayout';
import { API_URL } from '../config';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token') || ''}` });
const fmt = (d) => (d ? new Date(d).toLocaleString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

// A notification may carry a STAFF link (older rows, or staff-created ones).
// Rewrite it to the client equivalent so the client never bounces off a page
// they cannot open.
const toClientPath = (link) => {
  if (!link || typeof link !== 'string') return null;
  if (link.startsWith('/client')) return link;
  if (link.startsWith('/quotes')) return link.replace('/quotes', '/client-quotes');
  if (link.startsWith('/messages')) return '/client-messages';
  if (link.startsWith('/notifications')) return '/client-notifications';
  if (link.startsWith('/invoices')) return '/client-invoices';
  if (link.startsWith('/jobs') || link.startsWith('/active-jobs') || link.startsWith('/loads')) return '/client-jobs';
  return null;
};

function ClientNotifications({ onLogout }) {
  const navigate = useNavigate();
  const [user] = useState(() => JSON.parse(localStorage.getItem('user') || '{}'));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/notifications?user_id=${user.id}`, { headers: authHeaders() });
      const data = await r.json();
      if (data.success) setItems(data.notifications || []);
    } catch (err) {
      console.error('Notifications error:', err);
      toast.error('Could not load your notifications');
    } finally {
      setLoading(false);
    }
  };

  const markRead = async (n) => {
    if (!n || n.is_read) return;
    try {
      await fetch(`${API_URL}/notifications/${n.id}/read`, { method: 'PUT', headers: authHeaders() });
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
    } catch (err) {
      console.error(err);
    }
  };

  const markAll = async () => {
    try {
      await fetch(`${API_URL}/notifications/read-all?user_id=${user.id}`, { method: 'PUT', headers: authHeaders() });
      setItems((prev) => prev.map((x) => ({ ...x, is_read: true })));
      toast.success('All marked as read');
    } catch (err) {
      console.error(err);
      toast.error('Could not update your notifications');
    }
  };

  const open = (n) => {
    markRead(n);
    const path = toClientPath(n.link);
    if (path) navigate(path);
  };

  const visible = filter === 'unread' ? items.filter((n) => !n.is_read) : items;
  const unread = items.filter((n) => !n.is_read).length;

  return (
    <ClientLayout activeTab="notifications" onLogout={onLogout} user={user}>
      <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>🔔 Notifications</h1>
          <p className="text-sm mt-1" style={{ color: '#4A5568' }}>
            Quotes, job progress, delivery notes and invoices.{unread > 0 ? ` · ${unread} unread` : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setFilter(filter === 'unread' ? 'all' : 'unread')}
            className="px-4 py-2 rounded-lg border text-sm font-medium hover:bg-gray-50"
            style={{ borderColor: '#E8ECEF', color: '#0A1929' }}
          >
            {filter === 'unread' ? 'Show all' : 'Show unread'}
          </button>
          <button
            onClick={markAll}
            disabled={unread === 0}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
            style={{ backgroundColor: '#00A86B' }}
          >
            <FaCheckDouble size={12} /> Mark all read
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <FaSpinner className="animate-spin text-3xl mx-auto mb-4" style={{ color: '#00A86B' }} />
            <p className="text-gray-500">Loading your notifications...</p>
          </div>
        </div>
      ) : visible.length === 0 ? (
        <div className="bg-white rounded-xl border p-16 text-center" style={{ borderColor: '#E8ECEF' }}>
          <FaBell className="text-4xl mx-auto mb-4" style={{ color: '#CBD5E1' }} />
          <h3 className="text-lg font-semibold mb-2" style={{ color: '#0A1929' }}>
            {filter === 'unread' ? 'Nothing unread' : 'No notifications yet'}
          </h3>
          <p className="text-sm text-gray-500">
            You will be notified here when a quote is sent, a job moves, a POD is uploaded or an invoice is issued.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((n) => {
            const path = toClientPath(n.link);
            return (
              <button
                key={n.id}
                onClick={() => open(n)}
                className="w-full text-left bg-white rounded-xl border p-5 hover:shadow-sm transition-shadow"
                style={{ borderColor: '#E8ECEF', borderLeftWidth: 4, borderLeftColor: n.is_read ? '#E8ECEF' : '#00A86B' }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm" style={{ color: '#0A1929' }}>{n.title}</p>
                    <p className="text-sm mt-1" style={{ color: '#4A5568' }}>{n.message}</p>
                    <p className="text-xs mt-2" style={{ color: '#94A3B8' }}>
                      {fmt(n.created_at)}{n.is_read ? ' · read' : ''}
                    </p>
                  </div>
                  {path ? (
                    <span className="flex items-center gap-1 text-xs font-semibold whitespace-nowrap" style={{ color: '#00A86B' }}>
                      Open <FaArrowRight size={10} />
                    </span>
                  ) : (
                    <FaCheck size={12} style={{ color: n.is_read ? '#CBD5E1' : '#00A86B' }} />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </ClientLayout>
  );

}

export default ClientNotifications;
